import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import {
  ArrowUpDown,
  BanknoteX,
  CircleCheck,
  CircleOff,
  CircleX,
  CreditCard,
  DollarSignIcon,
  Hourglass
} from 'lucide-react'
import { useContext, useState } from 'react'
import { toast } from 'sonner'

import BrokersMembersTransactionsContext from 'components/context/BrokersMembersTransactionsContext'
import { getBrokersMembersEnrollmentsQueryKey } from 'components/hooks/needs_attention/useGetBrokersMembersEnrollments'
import { getTransactionsForEnrollmentQueryKey } from 'components/hooks/needs_attention/useGetTransactionsForEnrollment'
import { useModifyEnrollmentPayment } from 'components/hooks/needs_attention/useModifyEnrollmentPayment'
import { useModifyTransactionLossAmount } from 'components/hooks/useModifyTransactionLossAmount'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import {
  DropdownMenu as DropdownMenuContainer,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { Input } from 'components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { adminRecordInspector, adminSubstantiations } from 'rails_routes'
import { Badge, Button } from 'slab'
import { getGetBrokersUnprocessedEnrollmentsIdQueryKey, useGetBrokersMembersMemberIdEnrollmentsMappingOptions } from 'utils/web_api'
import { type ChargeOrTxn } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { capitalize, truncateText } from '../../utils'
import {
  type MemberEnrollment,
} from './needs_attention/types'
import { type EnrollmentCoverageType } from './types'

export const StripeCardDetails = ({
  transaction,
}: {
  transaction: ChargeOrTxn
}) => {
  const {
    external_url: transactionUrl,
    external_source_url: cardUrl,
  } = transaction

  if (!transactionUrl || !cardUrl) {
    return null
  }

  return (
    <div className='flex items-center space-x-2'>
      {transactionUrl && (
        <DisplayTooltipOnHover text='View transaction on Stripe'>
          <DollarSignIcon
            size={20}
            onClick={() => window.open(transactionUrl)}
          />
        </DisplayTooltipOnHover>
      )}
      {cardUrl && (
        <DisplayTooltipOnHover text='View card on Stripe'>
          <CreditCard size={20} onClick={() => window.open(cardUrl)} />
        </DisplayTooltipOnHover>
      )}
    </div>
  )
}

export const ReimbursementDetails = ({
  transaction,
}: {
  transaction: ChargeOrTxn
}) => {
  const {
    substantiation
  } = transaction

  if (!substantiation || !substantiation.id) {
    return null
  }

  return (
    <div className='flex items-center space-x-2'>
      <DisplayTooltipOnHover text='View reimbursement details'>
        <DollarSignIcon
          size={20}
          onClick={() => window.open(adminRecordInspector.show({ id: substantiation.id }))}
        />
      </DisplayTooltipOnHover>
    </div>
  )
}

// getOptionSorting ensures that enrollments of the same type are sorted first, followed by medical, dental, vision, and other.
function getOptionSorting (type: string, targetType: string) {
  switch (true) {
    case type === targetType:
      return 0
    case type === 'medical':
      return 1
    case type === 'dental':
      return 2
    case type === 'vision':
      return 3
    default:
      return 4
  }
}

type DefaultMappingOptionsType = Record<
  EnrollmentCoverageType,
  MemberEnrollment[]
>

const defaultOptions: DefaultMappingOptionsType = {
  medical: [],
  dental: [],
  vision: [],
}

function invalidateQueriesForEnrollments ({
  queryClient,
  memberId,
  enrollmentIds,
}: {
  queryClient: QueryClient
  memberId: string
  enrollmentIds: string[]
}) {
  queryClient.invalidateQueries({
    queryKey: getBrokersMembersEnrollmentsQueryKey({
      memberId,
    }),
  })
  enrollmentIds.forEach((enrollmentId) => {
    queryClient.invalidateQueries({
      queryKey: getTransactionsForEnrollmentQueryKey({
        memberId,
        enrollmentId,
        pagination: { pageIndex: 1, pageSize: 20 },
      }),
    })
    queryClient.invalidateQueries({
      queryKey: getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollmentId),
    })
  })
  toast.success('Successfully remapped transaction!', {
    position: 'top-center',
  })
}

export const RemapEnrollmentPaymentDropdown = ({
  transaction,
  enrollmentId,
  memberId,
}: {
  transaction: ChargeOrTxn
  enrollmentId: string
  memberId: string
}) => {
  const { reload } = useWindowLocation()

  const queryClient = useQueryClient()

  const { isMemberTransactionsViewV2Enabled, latestPlanYear } = useContext(
    BrokersMembersTransactionsContext
  )

  const { data: mappingOptions, isLoading } = useGetBrokersMembersMemberIdEnrollmentsMappingOptions(
    memberId,
    {
      query: {
        retry: false,
      }
    }
  )
  const remapEnrollmentPaymentMutation = useModifyEnrollmentPayment({
    memberId,
  })

  const { id: transactionId, loss_amount: lossAmount } = transaction

  if (lossAmount && lossAmount.subunits !== 0) {
    return (
      <DisplayTooltipOnHover text='This transaction has a loss amount and cannot be remapped.'>
        <CircleOff size={20} />
      </DisplayTooltipOnHover>
    )
  }

  const enrollment = mappingOptions?.find(({ id }) => id === enrollmentId)

  // Groups all possible mapping options, filtering out the current enrollment.
  const mappingOptionsGrouped =
    mappingOptions
      ?.filter((option) => option.id !== enrollmentId)
      .reduce<DefaultMappingOptionsType>(
        (acc, curr) => ({
          ...acc,
          [curr.coverage_type as EnrollmentCoverageType]: [
            ...acc[curr.coverage_type as EnrollmentCoverageType],
            curr,
          ],
        }),
        defaultOptions
      ) || defaultOptions

  // Sorts the grouped options by the coverage type and the current enrollment's coverage type.
  const sortedOptions = Object.entries(mappingOptionsGrouped).sort(
    (a, b) =>
      getOptionSorting(a[0], enrollment?.coverage_type || '') -
      getOptionSorting(b[0], enrollment?.coverage_type || '')
  )

  if (isLoading || remapEnrollmentPaymentMutation.isPending) {
    return <LoadingSpinnerCentered height='20px' width='20px' />
  }

  return (
    <DropdownMenuContainer>
      <DisplayTooltipOnHover text='Update enrollment payment mapping'>
        <DropdownMenuTrigger asChild className='cursor-pointer'>
          <ArrowUpDown size={20} data-testid='enrollment-mapping-button' />
        </DropdownMenuTrigger>
      </DisplayTooltipOnHover>
      <DropdownMenuContent>
        {sortedOptions.map(([type, options]) => {
          // If there are no options for the current type, return null.
          if (options.length === 0) {
            return null
          }

          return (
            <DropdownMenuGroup key={type}>
              <DropdownMenuLabel>{capitalize(type)}</DropdownMenuLabel>
              {options?.map(
                ({
                  id,
                  carrier_name: carrierName,
                  plan_name: planName,
                  plan_year: planYear,
                  has_coverage: hasCoverage,
                }) => (
                  <DropdownMenuItem
                    key={id}
                    onSelect={() => {
                      remapEnrollmentPaymentMutation.mutate(
                        {
                          transactionId,
                          enrollmentId: id,
                        },
                        {
                          onSuccess: () => {
                            // Reload the page if the v2 view is not enabled. Otherwise, invalidate the cache.
                            // TODO: Remove once v2 view is fully rolled out.
                            if (!isMemberTransactionsViewV2Enabled) {
                              reload()
                            }
                            invalidateQueriesForEnrollments({
                              queryClient,
                              memberId,
                              enrollmentIds: [enrollmentId, id],
                            })
                          },
                        }
                      )
                    }}
                  >
                    <div className='flex items-center space-x-2'>
                      <Badge
                        type={planYear === latestPlanYear ? 'info' : 'neutral'}
                      >
                        {planYear}
                      </Badge>
                      <Badge>{id.slice(-6)}</Badge>
                      <div className='text-ellipsis text-nowrap w-full grow-0'>
                        {truncateText(`${carrierName} | ${planName}`)}
                      </div>
                      {!hasCoverage && (
                        <Badge type='warning'>No Coverage</Badge>
                      )}
                    </div>
                  </DropdownMenuItem>
                )
              )}
            </DropdownMenuGroup>
          )
        })}

        {enrollmentId !== 'none' && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              key='none'
              onSelect={() => {
                remapEnrollmentPaymentMutation.mutate(
                  {
                    transactionId,
                    enrollmentId: 'none',
                  },
                  {
                    onSuccess: () => {
                      // Reload the page if the v2 view is not enabled. Otherwise, invalidate the cache.
                      // TODO: Remove once v2 view is fully rolled out.
                      if (!isMemberTransactionsViewV2Enabled) {
                        reload()
                        return
                      }
                      invalidateQueriesForEnrollments({
                        queryClient,
                        memberId,
                        enrollmentIds: [enrollmentId, 'none'],
                      })
                    },
                  }
                )
              }}
            >
              Mark as non-premium
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenuContainer>
  )
}

type MarkTransactionAsLossDirectlyProps = {
  transaction: ChargeOrTxn
  memberId: string
  enrollmentId: string
}

// Only certain transactions can be marked as loss directly;
// Any transactions for enrollments that are being prefunded should be have their
// losses handled at the enrollment level as opposed to the transaction level.
// This component is used for the latter only.
export const MarkTransactionAsLossDirectly = ({
  transaction,
  memberId,
  // This will be set to 'none' for non-premium payments.
  enrollmentId,
}: MarkTransactionAsLossDirectlyProps) => {
  const queryClient = useQueryClient()

  const modifyTransactionLossAmountMutation = useModifyTransactionLossAmount({
    transactionId: transaction.id,
    memberId,
  })
  const [isOpen, setIsOpen] = useState(false)

  const { loss_amount: lossAmountOriginal } = transaction
  const convertedLossAmount = (lossAmountOriginal?.subunits || 0) / 100
  const [lossAmount, setLossAmount] = useState<string>(
    convertedLossAmount.toString() || ''
  )

  const handleUpdateLossAmount = () => {
    modifyTransactionLossAmountMutation.mutate(
      { loss_amount: lossAmount },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getTransactionsForEnrollmentQueryKey({
              memberId,
              enrollmentId,
              pagination: { pageIndex: 1, pageSize: 20 },
            }),
          })
          toast.success('Successfully updated loss amount', {
            position: 'top-center',
          })
          setIsOpen(false)
        },
        onError: (error) => {
          toast.error(`Error updating loss amount: ${error.message}`, {
            position: 'top-center',
          })
        },
      }
    )
  }

  const isDisabled =
    parseFloat(lossAmount) > 0 && convertedLossAmount === parseFloat(lossAmount)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <DisplayTooltipOnHover text='Set loss amount'>
        <PopoverTrigger asChild>
          <BanknoteX
            size={20}
            data-testid='mark-loss-button'
            onClick={() => setIsOpen(true)}
          />
        </PopoverTrigger>
      </DisplayTooltipOnHover>
      <PopoverContent className='w-60'>
        <div className='flex flex-col items-start justify-start space-y-4'>
          <div className='font-semibold text-sm'>Set loss amount:</div>
          <Input
            value={lossAmount}
            onChange={(e) => setLossAmount(e.target.value)}
          />
          <Button
            variant='primary'
            disabled={isDisabled}
            onClick={handleUpdateLossAmount}
          >
            Update
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

const getSubstantiationStatusInfo = (status: string) => {
  switch (status) {
    case 'approved':
      return {
        icon: CircleCheck,
        color: 'text-green-500',
        label: 'Approved'
      }
    case 'waiting_for_review':
      return {
        icon: Hourglass,
        color: 'text-yellow-400',
        label: 'Waiting for Review'
      }
    case 'rejected':
      return {
        icon: CircleX,
        color: 'text-red-500',
        label: 'Rejected'
      }
    case 'inactive':
      return {
        icon: CircleOff,
        color: 'text-gray-500',
        label: 'Inactive'
      }
    default:
      return null
  }
}

export const ViewSubstantiation = ({
  transaction,
}: {
  transaction: ChargeOrTxn
}) => {
  const { substantiation } = transaction

  if (!substantiation) {
    return null
  }

  const statusInfo = getSubstantiationStatusInfo(substantiation.review_status)
  if (!statusInfo) {
    return null
  }

  const StatusIcon = statusInfo.icon

  return (
    <DisplayTooltipOnHover text={`Substantiation: ${statusInfo.label}`}>
      <StatusIcon
        size={16}
        strokeWidth={2}
        className={`${statusInfo.color} cursor-pointer`}
        data-testid={`substantiation-icon-${substantiation.review_status}`}
        onClick={() => window.open(adminSubstantiations.show({ id: substantiation.id }))}
      />
    </DisplayTooltipOnHover>
  )
}
